/**
 * Checkout / Undo checkout: API only, one dialog (pending → success/error), refresh documents on success.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ON_IFS_DOCUMENT_LOAD_CHANGED } from "../../constants/actionTypes";
import {
  CHECKOUTING_DOCUMENT,
  UNDO_CHECKOUT_PENDING,
  CHECKOUT_ERROR_MESSAGE,
  CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE,
  UNDO_CHECKOUT_ERROR_MESSAGE,
  ONEDRIVE_DOCUMENT_LOCKED_ERROR,
  UPLOADING_DOCUMENT_TO_ONEDRIVE,
} from "../../constants/messageTypes";
import {
  checkoutDocuments,
  uploadToOneDrive,
  getWorkingDocuments,
  deleteOneDriveDoc,
  UPLOAD_TO_ONEDRIVE_TIMEOUT_MS,
} from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { getDocClassCodeOnly } from "../../../functions/helperMethod";
import { serializeError } from "../../utils/serializeError";
import { removeWorkingDocumentByIdentifiers } from "../workingDocument/workingDocumentSlice";

const OPERATION_CHECKOUT = "Checkout";
const DOCUMENT_LOAD_ON_LOAD = "OnLoad";
const CHECKOUT_DIALOG_ERROR_CLOSE_MS = 2000;

/** Updates checkout dialog message from inside performCheckoutThunk. */
export const SET_CHECKOUT_FLOW_MESSAGE = "documents/documentCheckoutFlow/setMessage";

const userAuth = new UserAuth();

function resolveOneDriveUploadErrorMessage(uploadResult, uploadErr) {
  if (uploadResult?.status === 200) {
    return null;
  }
  if (uploadResult) {
    const data = uploadResult.data;
    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }
    if (data != null && typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    return CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE;
  }
  const responseData = uploadErr?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (responseData != null && typeof responseData.message === "string") {
    return responseData.message;
  }
  if (typeof uploadErr?.message === "string" && uploadErr.message.trim()) {
    return uploadErr.message;
  }
  return CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE;
}

function isUndoCheckoutBlockedByOpenDocument(error) {
  const status = error?.response?.status ?? 0;
  if (status === 423) return true;

  const messageParts = [
    error?.response?.data,
    error?.response?.statusText,
    error?.message,
  ]
    .filter((value) => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .toLowerCase();

  return (
    messageParts.includes("in use") ||
    (messageParts.includes("open") && messageParts.includes("close"))
  );
}

function buildCheckoutPayload(doc, operationStatus) {
  return {
    webUser: new AuthTeams().getEmail(),
    docClass: getDocClassCodeOnly(doc.class ?? doc.docClass ?? ""),
    docNo: doc.docNo,
    docSheet: doc.sheetNo ?? doc.docSheet,
    docRev: doc.docRev ?? doc.docRev,
    docType: doc.docType,
    fileNo: `${doc.fileNo ?? 1}`,
    fileExtension: doc.fileExtension ?? "",
    title: doc.title ?? "",
    operationStatus,
    state: doc.state,
    fileState: doc.fileState,
    owner: doc.owner,
    lastActionDate:
      doc.lastActionDate ?? new Date().toISOString().split("T")[0],
    isDownloaded: doc.isDownloaded ?? false,
  };
}

/**
 * Build upload-to-onedrive items from checkout list (same shape as upload API expects).
 */
function buildUploadItems(checkoutList) {
  const safeDecode = (value) => {
    if (value == null) return "";
    const text = String(value);
    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    }
  };

  return (checkoutList ?? []).map((doc) => ({
    class: getDocClassCodeOnly(doc.class ?? doc.docClass ?? ""),
    docNo: doc.docNo,
    sheetNo: doc.sheetNo ?? doc.docSheet,
    docRev: doc.docRev,
    docType: doc.docType,
    fileNo: doc.fileNo,
    // Checkout list stores encoded title; decode before OneDrive upload payload.
    title: safeDecode(doc.title),
    fileExtension: doc.fileExtension ?? "",
    state: doc.state,
    fileState: doc.fileState,
    owner: doc.owner,
    lastActionDate: doc.lastActionDate,
    isDownloaded: doc.isDownloaded ?? false,
  }));
}

function safeDecode(value) {
  if (value == null) return "";
  const text = String(value);
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function buildWorkingDocumentFileName(doc) {
  const title = safeDecode(doc?.title ?? "");
  const docClass = getDocClassCodeOnly(doc?.class ?? doc?.docClass ?? "");
  const docNo = doc?.docNo ?? "";
  const docSheet = doc?.sheetNo ?? doc?.docSheet ?? "";
  const docRev = doc?.docRev ?? "";
  const ext = doc?.fileExtension ?? "";
  return `${title} (${docClass} - ${docNo} - ${docSheet} - ${docRev}) - 1.${ext}`;
}

async function findWorkingDocumentByName(accessToken, uploadContext, doc) {
  if (!uploadContext?.tenantId) return null;

  const expectedName = buildWorkingDocumentFileName(doc);
  const oneDriveRoot =
    uploadContext.oneDriveRoot ??
    (uploadContext.channelId ? "ChannelDoc" : "MainMenu");

  const commonParams = {
    teamId: uploadContext.teamId ?? "",
    channelId: uploadContext.channelId ?? "",
    tenantId: uploadContext.tenantId,
    fileRoot: oneDriveRoot,
    displayCount: 200,
    recFrom: 0,
    recSize: 200,
    fileType: "''",
  };

  const titleProbe = safeDecode(doc?.title ?? "");

  const probes = [
    { ...commonParams, searchText: titleProbe },
    { ...commonParams, searchText: "" },
  ];

  for (const probe of probes) {
    const { data } = await getWorkingDocuments(accessToken, probe);
    const items = Array.isArray(data) ? data : [];
    const matched = items.find((item) => item?.name === expectedName);
    if (matched) return matched;
  }

  return null;
}

/** Payload: { checkoutList, operationStatus, uploadContext?, suppressCheckoutUi? } where uploadContext = { tenantId, teamId, channelId, oneDriveRoot } */
export const performCheckoutThunk = createAsyncThunk(
  "documents/documentCheckoutFlow/performCheckout",
  async (
    { checkoutList, operationStatus, uploadContext },
    { dispatch, rejectWithValue },
  ) => {
    const isCheckout = operationStatus === OPERATION_CHECKOUT;
    const errorMessage = isCheckout
      ? CHECKOUT_ERROR_MESSAGE
      : UNDO_CHECKOUT_ERROR_MESSAGE;

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }

      let deletedBeforeUndo = false;
      if (!isCheckout && uploadContext?.tenantId != null) {
        const membershipType = uploadContext.membershipType ?? "Regular";
        await Promise.all(
          (checkoutList ?? []).map(async (doc) => {
            const workingDoc = await findWorkingDocumentByName(
              accessToken,
              uploadContext,
              doc,
            );
            const fileId =
              workingDoc?.id ?? workingDoc?.documentId ?? workingDoc?.ItemsId;
            if (!fileId) return;

            try {
              await deleteOneDriveDoc(accessToken, {
                fileId,
                teamId: uploadContext.teamId ?? "",
                membershipType,
                oneDriveRoot:
                  uploadContext.oneDriveRoot ??
                  (uploadContext.channelId ? "ChannelDoc" : "MainMenu"),
                channelId: uploadContext.channelId ?? "",
                bypassSharedLock: false,
              });
              deletedBeforeUndo = true;
            } catch (deleteErr) {
              if (isUndoCheckoutBlockedByOpenDocument(deleteErr)) {
                throw deleteErr;
              }
              // Keep existing behavior for non-lock delete issues: continue with Undo check-out.
              console.warn(
                "Pre-undo OneDrive delete failed; proceeding with undo checkout",
                deleteErr,
              );
            }
          }),
        );
      }

      const payload = checkoutList.map((doc) =>
        buildCheckoutPayload(doc, operationStatus),
      );
      const { status } = await checkoutDocuments(payload, accessToken);

      if (status === 200) {
        if (!isCheckout) {
          (checkoutList ?? []).forEach((doc) => {
            dispatch(removeWorkingDocumentByIdentifiers(doc));
          });

          if (uploadContext?.tenantId != null && !deletedBeforeUndo) {
            const membershipType = uploadContext.membershipType ?? "Regular";
            await Promise.all(
              (checkoutList ?? []).map(async (doc) => {
                const workingDoc = await findWorkingDocumentByName(
                  accessToken,
                  uploadContext,
                  doc,
                );
                const fileId =
                  workingDoc?.id ??
                  workingDoc?.documentId ??
                  workingDoc?.ItemsId;
                if (!fileId) return;

                await deleteOneDriveDoc(accessToken, {
                  fileId,
                  teamId: uploadContext.teamId ?? "",
                  membershipType,
                  oneDriveRoot:
                    uploadContext.oneDriveRoot ??
                    (uploadContext.channelId ? "ChannelDoc" : "MainMenu"),
                  channelId: uploadContext.channelId ?? "",
                  bypassSharedLock: false,
                });
              }),
            );
          }
        }

        dispatch({
          type: ON_IFS_DOCUMENT_LOAD_CHANGED,
          payload: DOCUMENT_LOAD_ON_LOAD,
        });

        if (isCheckout && uploadContext?.tenantId != null) {
          dispatch({
            type: SET_CHECKOUT_FLOW_MESSAGE,
            payload: UPLOADING_DOCUMENT_TO_ONEDRIVE,
          });
          try {
            const uploadResult = await uploadToOneDrive(
              accessToken,
              {
                tenantId: uploadContext.tenantId,
                teamId: uploadContext.teamId ?? "",
                channelId: uploadContext.channelId ?? "",
                oneDriveRoot:
                  uploadContext.oneDriveRoot ??
                  (uploadContext.channelId ? "ChannelDoc" : "MainMenu"),
                items: buildUploadItems(checkoutList),
              },
              { timeout: UPLOAD_TO_ONEDRIVE_TIMEOUT_MS },
            );
            const failureMsg = resolveOneDriveUploadErrorMessage(
              uploadResult,
              null,
            );
            if (failureMsg) {
              setTimeout(() => {
                dispatch(closeCheckoutDialog());
              }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
              return rejectWithValue({
                message: failureMsg,
                checkoutSucceededIfs: true,
              });
            }
          } catch (uploadErr) {
            const failureMsg = resolveOneDriveUploadErrorMessage(null, uploadErr);
            setTimeout(() => {
              dispatch(closeCheckoutDialog());
            }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
            return rejectWithValue({
              message: failureMsg,
              checkoutSucceededIfs: true,
            });
          }
        }

        // Success text is shown only via toast (toastMiddleware). fulfilled reducer closes the dialog
        // without clearing lastOperationWasCheckout so All Documents can still navigate/refresh.
        return {
          variant: "info",
        };
      }
      setTimeout(() => {
        dispatch(closeCheckoutDialog());
      }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
      return rejectWithValue({ message: errorMessage });
    } catch (err) {
      const ser = serializeError(err);
      setTimeout(() => {
        dispatch(closeCheckoutDialog());
      }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
      if (!isCheckout && isUndoCheckoutBlockedByOpenDocument(err)) {
        return rejectWithValue({ message: ONEDRIVE_DOCUMENT_LOCKED_ERROR });
      }
      return rejectWithValue({ message: ser?.message ?? errorMessage });
    }
  },
);

const initialState = {
  loading: false,
  error: null,
  message: "",
  isDialogOpen: false,
  dialogVariant: "info",
  lastOperationWasCheckout: false,
};

const documentCheckoutFlowSlice = createSlice({
  name: "documents/documentCheckoutFlow",
  initialState,
  reducers: {
    closeCheckoutDialog: (state) => {
      state.isDialogOpen = false;
      state.lastOperationWasCheckout = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SET_CHECKOUT_FLOW_MESSAGE, (state, action) => {
        if (typeof action.payload === "string" && action.payload.trim()) {
          state.message = action.payload;
        }
      })
      .addCase(performCheckoutThunk.pending, (state, action) => {
        const isCheckout =
          action.meta?.arg?.operationStatus === OPERATION_CHECKOUT;
        const suppressUi = action.meta?.arg?.suppressCheckoutUi === true;
        const progressMessage = action.meta?.arg?.progressMessage;
        state.lastOperationWasCheckout = isCheckout;
        if (suppressUi) {
          return;
        }
        state.loading = true;
        state.error = null;
        state.message =
          progressMessage ??
          (isCheckout ? CHECKOUTING_DOCUMENT : UNDO_CHECKOUT_PENDING);
        state.isDialogOpen = true;
        state.dialogVariant = "info";
      })
      .addCase(performCheckoutThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = "";
        state.isDialogOpen = false;
        state.dialogVariant = action.payload?.variant ?? "info";
        // lastOperationWasCheckout left as set in pending (nav/refresh logic in DocumentsContent).
      })
      .addCase(performCheckoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
        state.message = action.payload?.message ?? CHECKOUT_ERROR_MESSAGE;
        state.isDialogOpen = false;
        state.dialogVariant = "error";
      });
  },
});

const { closeCheckoutDialog } = documentCheckoutFlowSlice.actions;

export { closeCheckoutDialog };
export default documentCheckoutFlowSlice.reducer;
